import { useState, useEffect, useMemo } from 'react';
import type { MouseEvent as ReactMouseEvent } from 'react';
import { useDrag, useDrop } from 'react-dnd';
import IdleFilterIcon from '../../assets/filter_idle.svg';
import SortAIcon from '../../assets/appliedsortA-Z.svg';
import SortZIcon from '../../assets/appliedsortZ-A.svg';
import FilterGreenDot from '../../assets/filter_applied_green_dot.svg';
import { ReactComponent as ClearColumnFilterIcon } from '../../assets/clearColumnFilter.svg';
import { ReactComponent as AscIcon } from '../../assets/ascending.svg';
import { ReactComponent as AscActiveIcon } from '../../assets/ascending_selected.svg';
import { ReactComponent as DescIcon } from '../../assets/descending.svg';
import { ReactComponent as DescActiveIcon } from '../../assets/descending_selected.svg';
import '../HeaderRenderers/DraggableHeaderRenderer.css';
import type { HeaderRendererProps } from 'react-data-grid';
import SortAscIcon from '../../assets/heroicons-solid_sort-ascending.svg';
import SortDisableAscIcon from '../../assets/heroicons-solid_sort-ascending_disabled.svg';
import SortDescIcon from '../../assets/heroicons-solid_sort-descending.svg';
import InfoIcon from '@mui/icons-material/Info';
import { InfoTooltip } from 'components/Tooltip/InfoTooltip';
import { nodeToText } from 'utils/common-util';

import Popover from '@mui/material/Popover';
import Tooltip from '@mui/material/Tooltip';
import MenuItem from '@mui/material/MenuItem';
import { FormControl, FormControlLabel, TextField, Button, InputAdornment, Stack, Typography, Box, Divider } from '@mui/material';
import Checkbox from '@mui/material/Checkbox';
import CheckIcon from '@mui/icons-material/Check';
import useStyles from '../jobs/jobs_styles';
import searchIcon from '../../assets/akar-icons_search-1.svg';
import { ReactComponent as DatePickerIcon } from '../../assets/calendar-outline_white.svg';

import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import moment from 'moment-timezone';
import debounce from 'lodash.debounce';
import { getIsV2 } from 'utils/versionToggle';
import { useTheme } from "@mui/material/styles";
import CustomDropdown from 'components/Dropdown';
import ColumnFilterControl from '../ColumnFilters/index';
import { FilterMenuIcon } from '../CommonIcons/Icons';
import type { FilterableColumn } from '../ColumnFilters/types';
import type { UseColumnFiltersResult } from '../ColumnFilters/useColumnFilters';

// Customized Sort label configuration
export interface SortOption {
  label: string;
  direction: 'asc' | 'desc';
}

export interface SortLabels {
  ascending: SortOption;
  descending: SortOption;
}

export const SORT_FIELD_CONFIG = {
  TIME: ['date', 'start_time', 'end_time', 'estimated_end_time'],
  TEXT: ['jobType', 'job_type', 'jobtype', 'job_id', 'job_name', 'source_type', 'program_name', 'is_recurrence', 'application_name',
	'execution_status', 'trigger_name', 'trigger_type', 'data_asset_name', 'silver_table_name', 'bronze_table_name', 'subscription_name', 'subscription_id', 'resource_group', 'resource_name'],
  NUMERIC: ['hour', 'duration_ms', 'avg_duration_ms', 'total_exe_count', 'successful', 'failed', 'in_progress', 'delayed', 'aborted', 'total']
} as const;

const isFieldType = (columnKey: string, type: keyof typeof SORT_FIELD_CONFIG): boolean => {
  const normalized = columnKey.toLowerCase();
  return SORT_FIELD_CONFIG[type].some(field => normalized.includes(field));
};

export const getSortLabels = (columnKey: string): SortLabels => {
  if (isFieldType(columnKey, 'TIME')) {
    return {
      ascending: { label: 'Oldest to Latest', direction: 'asc' },
      descending: { label: 'Latest to Oldest', direction: 'desc' }
    };
  }

  if (isFieldType(columnKey, 'TEXT')) {
    return {
      ascending: { label: 'A - Z', direction: 'asc' },
      descending: { label: 'Z - A', direction: 'desc' }
    };
  }

  // Default to numeric
  return {
    ascending: { label: 'Ascending', direction: 'asc' },
    descending: { label: 'Descending', direction: 'desc' }
  };
};

function getSortIconSource(currSorting: any, columnKey: string, isSortIcon: boolean, customIdleIcon?: string | boolean): string {
  if (customIdleIcon === false) {
    if (!currSorting || !currSorting.sortBy) {
      return '';
    }
    if (currSorting.sortBy !== columnKey) {
      return isSortIcon ? SortDisableAscIcon : '';
    }
    if (currSorting.sortDir === 'asc') {
      return isSortIcon ? SortAscIcon : SortAIcon;
    }
    return isSortIcon ? SortDescIcon : SortZIcon;
  }

  const idleIcon: string = (typeof customIdleIcon === 'string' && customIdleIcon) ? customIdleIcon : IdleFilterIcon;

  if (typeof customIdleIcon === 'string' && customIdleIcon) {
    return customIdleIcon;
  }

  // No sorting applied
  if (!currSorting || !currSorting.sortBy) {
    return isSortIcon ? SortDisableAscIcon : idleIcon;
}
 // Column not currently sorted
  if (currSorting.sortBy !== columnKey) {
    return isSortIcon ? SortDisableAscIcon : idleIcon;
  }

  // Column is sorted - return icon based on sort direction
  if (currSorting.sortDir === 'asc') {
    return isSortIcon ? SortAscIcon : SortAIcon;
  }

  // Descending sort
  return isSortIcon ? SortDescIcon : SortZIcon;
}

interface DraggableHeaderRendererProps<R> extends Readonly<HeaderRendererProps<R>> {
	readonly onColumnsReorder: (sourceKey: string, targetKey: string) => void;
	readonly onContextMenu: (selectedColumns: any) => void;
	readonly onFilterMenu: (sortingInfo: any) => void;
	readonly onFilterCheck: (filterInfo: any, columnKey: any) => void;
	readonly onFilterSearch: (searchDetails: any, columnKey: any) => void;
	readonly onHeaderSelectAll: (event: any) => void;
	readonly onFilterClick: (selectedColumn: any) => void;
	readonly filterData: any;
	readonly searchData: any;
	readonly columnNames: any;
	readonly visibleColumns: any;
	readonly currSorting: any;
	readonly currFilter: any;
	readonly tableColumnKeys: any;
	readonly tableType: any;
	readonly sectorsList: any;
	readonly headerCheckbox: any;
	readonly isSortIcon: boolean;
	readonly idleFilterIcon?: string;
	readonly hierarchyRender?: any;
	readonly dropdownOptions?: any;
	readonly dropdownValue?: string;
	readonly onDropdownChange?: (value: string) => void;
	readonly enableColumnFilter?: boolean;
	readonly columnFilterController?: UseColumnFiltersResult;
	readonly columnFilterColumns?: FilterableColumn[];
	readonly columnFilterRows?: any[];
	readonly getColumnFilterValues?: (field: string) => Promise<any[]>;
}

const getFilterDisabledColumns = (tableType: string, isV2: boolean): string[] => { // NOSONAR
	if (!tableType) return [];
	if (tableType === 'Presto-details' || tableType === 'Presto') {
		return ['query'];
	}
	if (tableType === 'PCOBS_ResourceGroup') {
		return ['selected', 'ClusterName', 'ClusterJobSource'];
	}
	if (tableType === 'IBP_JobRuns') {
		return ['ticketStatus', 'destination', 'source'];
	}
	if (tableType === 'project-list') {
		return ['action'];
	}
	if (tableType === 'added-schedules') {
		return ['slaDate', 'slaTime', 'scheduleInfo', 'priJobStatus', 'jobCondition', 'action', 'folderName', 'subFolerName', 'jobRunUrl', 'sourceTable', 'targetTable'];
	}
	if (tableType === 'add-schedules') {
		return isV2
			? ['folderNames', 'subFolderNames', 'scheduleInfo', 'Scheduler', 'cyclic', 'jobStatus', 'selected']
			: ['adfName', 'triggerName', 'scheduleInfo', 'selected', 'cyclic', 'Scheduler', 'jobStatus'];
	}
	if (tableType === 'databricks') {
		return ['time'];
	}
	if (tableType === 'job-schedules-1') {
		return ['actualStartTime', 'actualEndTime', 'stepId', 'platformId', 'totalExecutions', 'oDate', 'avgRunTime', 'jobCondition', 'execStatusTrend', 'isCyclic', 'scheduleInfo', 'jobRunUrl', 'sourceTable', 'targetTable','dqStatusGE','dqStatusTrendGE'];
	}
	if (tableType === 'job-bulk-upload') {
		return ['Platform', 'Scheduler'];
	}
	if (tableType === 'incident-details-ver') {
		return ['outageStartTime', 'outageEndTime', 'outageDuration', 'serviceOffering', 'category', 'subCategory', 'mttr'];
	}
	if (tableType === 'impact-teradata') {
		return ['iterationDate', 'runEndTime', 'reasonForDelay', 'slaEndTime'];
	}
	if (tableType === 'program-list') {
		return ['action', 'IP_Delayed_Start', 'IP_Long_Running', 'Failed', 'Success', 'SLA_Time'];
	}
	if (['programs-dashboard', 'new-dashboard'].includes(tableType)) {
		return ['Sectors', 'SLA_Status', 'Project_Name', 'action', 'IP_Delayed_Start', 'IP_Long_Running', 'Failed', 'Success', 'SLA_Time'];
	}
	if (tableType === 'resource-details') {
		return ['selected', 'BillingInformation'];
	}
	if (tableType === 'resource-list' || tableType === 'programs-list') {
		return ['actions'];
	}
	if (tableType === 'databricksNewInc') {
		return ['workspaceId', 'workspaceName', 'clusterId', 'jobId', 'businessSector', 'environment', 'supportContact', 'dfDataAssetName', 'serviceName', 'billingInformation', 'orgGrpCodeProjectCode', 'dfProjectCode', 'dfSectorName', 'businessContact', 'projectName', 'projectNumber', 'startDate', 'runName', 'status', 'errorMessage', 'statementId', 'warehouseID'];
	}
	if (tableType === 'KPIList') {
		return ['isActive', 'createdByUser', 'updatedByUser', 'updatedAt', 'action', '_id', 'source_type', 'update_frequency', 'next_run_time', 'last_run_time'];
	}
	if (tableType === 'AlertDetail') {
		return ['alertType', '_id'];
	}
	if (tableType === 'KPIResults') {
		return ['ingestId'];
	}
	if (tableType === 'AlertList') {
		return ['_id', 'updatedByUser', 'createdByUser'];
	}
	if (tableType === 'kpi-total-dataset-tables') {
		return ['tableName', 'mQuery'];
	}
	if (tableType === 'kpi-reports-with-similar-datasets') {
		return ['datasetName', 'datasetCreationDate'];
	}
	if (tableType === 'ovearall-cost-details-ver') {
		return ['sqlEndpointId'];
	}
	if (tableType === 'ibp-gdo-details') {
		return ['sectors', 'markets', 'actExecutionTime', 'nxtscheduletime', 'statusCountArray', 'slaStatus', 'gdoDate', 'programDescription', 'actions'];
	}
	if (tableType === 'ibp-details-grid') {
		return ['sectors', 'projectName', 'failedCount', 'successCount', 'slaTime', 'projectFriendlyName', 'projectOwner'];
	}
	if (tableType === 'ibp-gdo-stage-details') {
		return ['startTime',  'endTime', 'nextExecutionTime'];
	}
	if (tableType === 'ibp-gdo-stage-job-details') {
		return ['platformId', 'actualStartTime', 'actualEndTime', 'avgRunTime', 'totalExecutionTime', 'totalExecutions', 'nextExecutionTime', 'triggerName', 'pipeline_trigger_id', 'execStatusTrend', 'jobType', 'oDate', 'isCyclic', 'jobCondition', 'jobOwner', 'scheduleInfo', 'schedulerName', 'comment'];
	}
	if (
		[
			'dbu-details-ver',
			'compute-cost-table',
			'compute-count-table',
			'compute-spend-table',
			'top-spend-table',
			'runtime-spend-table',
			'cost-workspace-details-ver',
			'dbu-workspace-details-ver'
		].includes(tableType)
	) {
		return ['workspace_id'];
	}
	if (tableType === 'active-workspaces-details-ver') {
		return ['workspaceID'];
	}
	if (tableType === 'adb-cost-spend-table') {
		return ['workspace_name'];
	}
	if (tableType === 'UnifiedAlertsSnow' || tableType?.startsWith('UnifiedIncidentsSnow_')) {
		return ['comments', 'activity', 'actions', 'kbArticles'];
	}
	if (tableType?.startsWith('pipeline-run-status')) {
		const isScheduled = tableType === 'pipeline-run-statusScheduled' || tableType === 'pipeline-run-status-data-assets-Scheduled';
		return isScheduled
			? ['is_recurrence', 'recurrence_per_day', 'job_type', 'execution_status', 'date', 'trigger_type', 'data_asset_name', 'silver_table_name', 'bronze_table_name', 'subscription_name', 'subscription_id', 'folder_name', 'job_no']
			: ['total_exe_count', 'run_id', 'date', 'folder_name', 'job_no', 'gold_count', 'success_count', 'failed_count'];
	}
	if (tableType === 'ManageSopPage' || tableType === 'ManageSopListV2') {
		return ['updatedAt', 'actions'];
	}
	return [];
};

/** Column keys that support server-side search */
const SEARCHABLE_COLUMNS = [
	'application',
	'subApplication',
	'name',
	'fullName',
	'gpid',
	'email',
	'prjName',
	'prjDescription',
	'dsName',
	'flowName',
	'viewName',
	'viewUrlName',
	'workbookName',
	'projectName',
	'contentName',
	'jobStatus',
	'jobType',
	'contentType',
	'lastExecutionDuration',
	'cluster_name',
	'project_name',
	'workspace_name',
	'scheduleName',
	'scheduleType',
	'scheduleFrequency',
	'schedulePriority',
	'job_name',
	'login_user',
	'instance',
	'project',
	'extract',
	'extractType',
	'views',
	'Resource',
	'ResourceGroup',
	'ProjectName',
	'DFProjectcode',
	'BillingInformation',
	'Subscription',
	'MeterCategory',
	'MeterName',
	'Job_Name',
	'sector',
	'workspaceName',
	'itemName',
	'capacityName',
	'datasetName',
	'capacityStatus',
	'itemKind',
	'user',
	'viewCount',
	'lastRefreshDuration',
	'owner',
	'userName',
	'manager',
	'reportName',
	'projectName',
	'projectOwner',
	'sector'
] as const;

/** Column keys that support server-side search data results */
const SEARCH_DATA_COLUMNS = [
	'application',
	'subApplication',
	'name',
	'fullName',
	'email',
	'gpid',
	'prjName',
	'prjDescription',
	'dsName',
	'flowName',
	'viewName',
	'viewUrlName',
	'workbookName',
	'projectName',
	'contentName',
	'jobStatus',
	'jobType',
	'contentType',
	'lastExecutionDuration',
	'cluster_name',
	'project_name',
	'workspace_name',
	'scheduleName',
	'scheduleType',
	'scheduleFrequency',
	'schedulePriority',
	'job_name',
	'login_user',
	'instance',
	'project',
	'extract',
	'extractType',
	'views',
	'Resource',
	'ResourceGroup',
	'ProjectName',
	'DFProjectcode',
	'BillingInformation',
	'Subscription',
	'MeterCategory',
	'MeterName',
	'sector',
	'workspaceName',
	'itemName',
	'capacityName',
	'capacityStatus',
	'itemKind'
] as const;

/** Filter an individual filter-data array by the current search input text */
function filterByInputText(items: any[], inputText: string, columnKey: string): any[] {
	if (inputText === '') {
		return items.filter((item: any) => item !== null && item !== undefined ? item.toString() : null);
	}
	return items.filter((item: any) => {
		if (!item) return false;
		if (columnKey === 'criticalFlag' && ['y', 'ye', 'yes'].includes(inputText)) {
			return item === true;
		}
		if (columnKey === 'criticalFlag' && ['n', 'no'].includes(inputText)) {
			return item === false;
		}
		return item.toLowerCase().includes(inputText);
	});
}

/** Build the filterSearchData object by merging filterData, sectorsList, inputText and searchData */
function buildFilterSearchData(
	filterData: any,
	columnKey: string,
	sectorsList: any,
	inputText: string,
	searchData: any
): Record<string, any> {
	const result = { ...filterData };

	if (filterData?.[columnKey]?.length > 0) {
		if (columnKey === 'sector' && sectorsList?.length > 0) {
			result[columnKey] = sectorsList;
		} else {
			result[columnKey] = filterByInputText(filterData[columnKey], inputText, columnKey);
		}
	}

	const hasSearchResults = searchData?.name?.length > 0;
	if (hasSearchResults && (SEARCH_DATA_COLUMNS as readonly string[]).includes(columnKey) && inputText.length > 1) {
		result[columnKey] = searchData.name;
	}

	return result;
}

export function DraggableHeaderRenderer<R>({
	onColumnsReorder,
	onContextMenu,
	onFilterMenu,
	onFilterCheck,
	onFilterSearch,
	onHeaderSelectAll,
	onFilterClick,
	filterData,
	searchData,
	columnNames,
	visibleColumns,
	column,
	currSorting,
	currFilter,
	tableColumnKeys,
	tableType,
	sectorsList,
	headerCheckbox,
	isSortIcon,
	idleFilterIcon,
	hierarchyRender,
	dropdownOptions,
	dropdownValue,
	onDropdownChange,
	enableColumnFilter,
	columnFilterController,
	columnFilterColumns,
	columnFilterRows,
	getColumnFilterValues,
	...props
}: Readonly<DraggableHeaderRendererProps<R>>) {
	const theme = useTheme();
	const colors = theme.palette;

	const setFilterPopupOpen = (value: string) => {
		localStorage.setItem(`${tableType}_filterPopupOpen`, value);
	};

	const dropdownSxProps = {
		height: '30px !important',
		color: `${colors.dropdown.checkBox} !important`,
		'& .MuiSelect-select': {
			color: `${colors.dropdown.checkBox} !important`,
		},
		'& .MuiOutlinedInput-notchedOutline': {
			borderColor: `${colors.dropdown.checkBox} !important`
		},
		'&:hover .MuiOutlinedInput-notchedOutline': {
			borderColor: `${colors.dropdown.checkBox} !important`
		},
		'&.Mui-focused .MuiOutlinedInput-notchedOutline': {
			borderColor: `${colors.dropdown.checkBox} !important`
		},
		'& .MuiSelect-icon': {
			color: `${colors.dropdown.checkBox} !important`
		},
		'& .MuiBox-root': {
			fontWeight: `500 !important`,
		},
	};

	const [{ isDragging }, drag] = useDrag({
		type: 'COLUMN_DRAG',
		item: { key: column.key },
		collect: (monitor) => ({
			isDragging: monitor.isDragging()
		})
	});
	const [contextMenu, setContextMenu] = useState<{ // NOSONAR
		mouseX: number;
		mouseY: number;
	} | null>(null);

	const [filterMenu, setFilterMenu] = useState<{
		mouseX: number;
		mouseY: number;
	} | null>(null);
	const classes = useStyles();
	const [{ isOver }, drop] = useDrop({
		accept: 'COLUMN_DRAG',
		drop({ key }: { key: string }) {
			onColumnsReorder(key, column.key);
		},
		collect: (monitor) => ({
			isOver: monitor.isOver(),
			canDrop: monitor.canDrop()
		})
	});

	const filterPopup = (event: any) => {
		const filterClickedColumn = event.target.parentNode?.dataset?.headerKey
		const validTableTypes = [
			'PCOBS_ResourceGroup',
			'all_used_workbooks',
			'tableausetDetailsList',
			'Performance',
			'tableauUsers',
			'projects',
			'workbooks',
			'datasources',
			'views',
			'flows',
			'job-schedules-1'
		];
		setFilterPopupOpen('true');
		if (validTableTypes.includes(tableType) && filterClickedColumn) {
			onFilterClick(filterClickedColumn)
		}
		setFilterMenu(
			filterMenu === null
				? {
					mouseX: Number.parseInt(event.clientX) - 300,
					mouseY: Number.parseInt(event.clientY) + 12
				}
				: null
		);
		if (checkedList.length == filterSearchData[filterClickedColumn]?.length) {
			setItemsChecked(true);
		}
		else {
			setItemsChecked(false);
		}
	};

	const onHandleHeaderSelectAllCheckbox = (event: any) => {
		onHeaderSelectAll(event)
	}

	const handleFilterAction = (event: any) => {
		event.preventDefault();
		const ptLength = Object.keys(event.target.parentNode.dataset).length;
		onFilterMenu(ptLength === 0 ? event.target.dataset : event.target.parentNode.dataset);
		setFilterMenu(null);
		setFilterPopupOpen('true');
	};

	const handleClearColunmAction = (event: any) => {
		const targetFilter = currFilter || {};
		targetFilter[column.key] = [];
		if (typeof onFilterCheck === 'function') {
			onFilterCheck(targetFilter, '');
		}
		setCheckedList([]);
		setItemsChecked(false);
		setFilterPopupOpen('true');

		// Clear viewCount specific state
		if (column.key === 'viewCount') {
			setMinViewCount('');
			setMaxViewCount('');
		}

		handleClose(event);
	};

	const contextClick = (event: any) => {
		event.preventDefault();
		event.stopPropagation();
		console.log('contextClick')
		onContextMenu(visibleColumns);
	};

	const [checkedList, setCheckedList] = useState<any[]>([]);
	const [fromDateTime, setFromDateTime] = useState<any>(moment().startOf('day'));
	const [toDateTime, setToDateTime] = useState<any>(moment().endOf('day'));
	const dateTimeFields = new Set(['createdAt', 'updatedAt', 'deletedAt', 'training_date', 'lastLogin', 'reportCreationDate', 'operationTime', 'lastAccessedDate', 'createdDate', 'modifiedDate']);
	const url = globalThis.location.pathname;
	const jobName = url.substring(url.lastIndexOf('/') + 1);
	const [minViewCount, setMinViewCount] = useState<number | ''>('');
	const [maxViewCount, setMaxViewCount] = useState<number | ''>('');
	const isPipelineTableType = tableType?.startsWith('pipeline-run-status') || tableType === 'PipelineStatus' || tableType?.startsWith('data-reconciliation-');

	const handleViewCountChange = (field: 'min' | 'max', value: string): void => {
		const numValue = value === '' ? '' : Number.parseInt(value, 10);
		if (field === 'min') {
			setMinViewCount(numValue);
		} else {
			setMaxViewCount(numValue);
		}
	};

	const filterDisabledColumns = getFilterDisabledColumns(tableType, getIsV2());


	// Confirm changes when OK is clicked
	//  const handleSaveClick = (event: any) => {
	// 	console.log("handleSaveClick");
	// 	event.preventDefault();  // Prevent the event from propagating further
	// 	event.stopPropagation(); 
	// 	onContextMenu(tempVisibleColumns); // Update the visible columns with the selected tempVisibleColumns
	// 	handleCloseIconClick(); // Close the dialog
	//   };

	// Cancel changes
	//   const handleCancelClick = () => {
	// 	console.log("2");
	// 	setTempVisibleColumns(visibleColumns); // Revert tempVisibleColumns to the original visibleColumns
	// 	setSelectAll(visibleColumns.length === columnNames.length); // Revert "Select All" checkbox state
	// 	handleCloseIconClick(); // Close the dialog
	//   };

    const handleClose = (event: any) => {
		setFilterPopupOpen('true');
		if (!enableColumnFilter) {
			setFilterMenu(null);
			if (currFilter?.[column.key]) setCheckedList(currFilter[column.key]);
			else setCheckedList([]);
			setInputText('');
		}
	};

	// FILTER LOGIC START

	const handleOkClick = (event: any) => {
		const targetFilter = currFilter || {};
		// Updating the full filter query
		if (dateTimeFields.has(column.key) && fromDateTime && toDateTime) {
			targetFilter[column.key] = [fromDateTime, toDateTime];
		} else if (column.key === 'viewCount' && (minViewCount !== '' || maxViewCount !== '')) {
			targetFilter[column.key] = [minViewCount !== '' ? minViewCount : null, maxViewCount !== '' ? maxViewCount : null];
		} else {
			if (checkedList.length > 0) {
				targetFilter[column.key] = checkedList;
			} else {
				// resetting the filter
				targetFilter[column.key] = [];
			}
		}
		if (typeof onFilterCheck === 'function') {
			onFilterCheck(targetFilter, column.key);
		}
		setContextMenu(null); // Close the menu
		setFilterPopupOpen('true');
	};

	const [itemsChecked, setItemsChecked] = useState(false);

	const handleChange = (e: any) => {
		const { value, checked } = e.target;
		const newlyChecked = [];
		if (checked && e.target.value) {
			newlyChecked.push(e.target.value);
		}
		if (checked) {
			setCheckedList([...checkedList, ...newlyChecked]);
			const allChecked = filterSearchData[column.key]?.length === checkedList.length + newlyChecked.length;
			setItemsChecked(allChecked);
		} else {
			setCheckedList(checkedList.filter((item) => item !== value));
			setItemsChecked(false);
		}
	};
	const selectAllCheck = (e: any) => {
		const { checked } = e.target;
		const collection: any[] = [];
		if (checked && filterSearchData[column.key]) {
			for (const item of filterSearchData[column.key]) {
				if (item !== null && item !== undefined) {
					collection.push(item.toString());
				}
			}
		}
		setCheckedList(checked ? collection : []);
		setItemsChecked(checked);
	};
	const handleSearch = (e: KeyboardEvent): void => {
		e.stopPropagation();
		const searchValue = (e.target as HTMLInputElement).value.toLowerCase();
		setInputText(searchValue);
		if (searchValue.length > 1 && (SEARCHABLE_COLUMNS as readonly string[]).includes(column.key)) {
			onFilterSearch(searchValue, column.key);
		}
	};
	const [inputText, setInputText] = useState('');

	const debouncedResults = useMemo(() => {
		return debounce(handleSearch, 300);
	}, []);
	useEffect(() => {
		return () => {
			debouncedResults.cancel();
		};
	});

	const filterSearchData = buildFilterSearchData(filterData, column.key, sectorsList, inputText, searchData);

	// Date Range
	const handleDateTimeChange = (field: any, dateTime: any): void => {
		if (field === 'fromDateTime') setFromDateTime(dateTime);
		else setToDateTime(dateTime);
	};
	// Date Range

	// FILTER LOGIC END
	useEffect(() => { // NOSONAR
		// Updating the full filter query from the previous filter
		if (currFilter?.[column.key]) {
			if (dateTimeFields.has(column.key)) {
				if (currFilter[column.key][0]) setFromDateTime(currFilter[column.key][0]);
				if (currFilter[column.key][1]) setToDateTime(currFilter[column.key][1]);
			}
			else if (column.key === 'viewCount') {
				if (currFilter[column.key][0] !== null) setMinViewCount(currFilter[column.key][0]);
				if (currFilter[column.key][1] !== null) setMaxViewCount(currFilter[column.key][1]);
			} else {
				setCheckedList(currFilter[column.key]);
			}
		}
	}, [currFilter]);

	const renderHeaderDropdown = () => {
		if (!(['job-schedules-1', 'ibp-gdo-stage-job-details'].includes(tableType) && column.key === 'jobName' && dropdownOptions)) return null;
		return (
			<CustomDropdown
				options={dropdownOptions.map((item: any) => ({
					value: item.value,
					label: item.label
				}))}
				handleChange={(e: any) => {
					onDropdownChange?.(e.target.value);
				}}
				value={dropdownValue || ''}
				disabled={true}
				boxProps={{
					maxWidth: 240,
					sx: {
						position: 'absolute',
						right: '36px',
						top: '50%',
						transform: 'translateY(-50%)',
						width: 180,
						maxWidth: 180,
					},
				}}
				selectComponentProps={{
					sx: dropdownSxProps
				}}
			/>
		);
	};

	// Memoize sort labels at component level
	const sortLabels = useMemo(() => getSortLabels(column.key), [column.key]);
	const isAscendingActive = currSorting?.sortBy === column.key && currSorting?.sortDir === sortLabels.ascending.direction;
	const isDescendingActive = currSorting?.sortBy === column.key && currSorting?.sortDir === sortLabels.descending.direction;

	// --- Extracted render helpers to reduce cognitive complexity ---

	const renderHeaderContent = () => {
		// header select all checkbox
		if (column.name == 'HeaderCheckbox') {
			return (
				<input
					type="checkbox"
					className="transparent-checkbox"
					id="HeaderCheckbox"
					onChange={onHandleHeaderSelectAllCheckbox}
				/>
			);
		}
		if (column.name == 'HeaderCheckboxMapping') {
			return (
				<Checkbox
					// inputRef={headerCheckboxRef}
					id="HeaderCheckboxMapping"
					sx={{
						padding: '0 4px 0 0'
					}}
					onChange={onHandleHeaderSelectAllCheckbox}
					checked={headerCheckbox}
				/>
			);
		}
		const headerText = typeof column.name === 'string' ? column.name : nodeToText(column.name);
		const normalizedHeader = String(headerText || '').toLowerCase().trim();
		const tableTypeMarkers = ['-rm-cost-summary', '-rm-detailed-view'];
		const shouldInjectInfo =
			(tableTypeMarkers.some(marker => String(tableType || '').includes(marker)) &&
				['programs', 'sectors', 'projects', 'resource types'].includes(normalizedHeader)) ||
			(tableType?.startsWith('pipeline-run-status') && normalizedHeader === 'hour');
		return (
			<>
				<span className={tableType?.toLowerCase().startsWith('data-reconciliation-') ? 'reconciliation-header-label' : undefined}>
					{typeof column.name === 'string' ? column.name.toUpperCase() : column.name}
				</span>
				{shouldInjectInfo ? (
					<Box sx={{ 
						display: 'inline-flex',
						transform: 'translateY(4px)'
					}}>
						<InfoTooltip
							icon={<InfoIcon  sx={{ color: '#A9ADCB', fontSize: '16px' }} />}
							label={
								tableType?.startsWith('pipeline-run-status') && normalizedHeader === 'hour'
									? 'Hour of the day (0–23)'
									: hierarchyRender ?? ''
							}
							hasNoCursor = {true}
							{...(tableType?.startsWith('pipeline-run-status') && {
								tooltipBackgroundColor: "#1b1e35 !important",
								tooltipTextColor: "#f5f5f5 !important",
							})}
							tooltipProps={{
								placement: 'bottom-start',
								arrow: true,
								slotProps: {
									tooltip: {
										sx: {
											bgcolor: '#002349 !important',
										}
									}
								}
							}}
						/>
					</Box>
				) : null}
			</>
		);
	};

	const renderPipelineSortMenuItems = () => (
		<>
			<MenuItem
				className="filter-row"
				onClick={handleFilterAction}
				data-sort-by={column.key}
				data-sort-dir={sortLabels.ascending.direction}
				sx={{
					backgroundColor: isAscendingActive ? 'rgba(0,145,165,0.08)' : 'transparent',
				}}
			>
				<Stack direction="row" alignItems="center" spacing={2} data-sort-by={column.key} data-sort-dir={sortLabels.ascending.direction} sx={{ width: '100%', justifyContent: 'space-between' }}>
					<Typography variant="bodyText">{sortLabels.ascending.label}</Typography>
					{isAscendingActive && <CheckIcon sx={{ fontSize: '16px', color: '#003771' }} />}
				</Stack>
			</MenuItem>

			<MenuItem
				className="filter-row"
				onClick={handleFilterAction}
				data-sort-by={column.key}
				data-sort-dir={sortLabels.descending.direction}
				sx={{
					backgroundColor: isDescendingActive ? 'rgba(0,145,165,0.08)' : 'transparent',
				}}
			>
				<Stack direction="row" alignItems="center" spacing={2} data-sort-by={column.key} data-sort-dir={sortLabels.descending.direction} sx={{ width: '100%', justifyContent: 'space-between' }}>
					<Typography variant="bodyText">{sortLabels.descending.label}</Typography>
					{isDescendingActive && <CheckIcon sx={{ fontSize: '16px', color: '#003771' }} />}
				</Stack>
			</MenuItem>

			<Divider sx={{ my: 1, bgcolor: `${colors.dropdown.menuItemText}`, opacity: 0.4, height: '1px !important' }} />

			<MenuItem
				className="filter-row"
				onClick={(e) => {
					e.preventDefault();
					onFilterMenu({ sortBy: '', sortDir: '' });
					setFilterMenu(null);
					setFilterPopupOpen('true');
				}}
				disabled={!currSorting?.sortBy || currSorting?.sortBy !== column.key}
			>
				<Typography variant="bodyText" sx={{ opacity: (currSorting?.sortBy === column.key) ? 1 : 0.5 }}>
					Clear Filter
				</Typography>
			</MenuItem>
		</>
	);

	const renderDefaultSortMenuItems = () => (
		<>
			<MenuItem className="filter-row" onClick={handleFilterAction} data-sort-by={column.key} data-sort-dir="asc">
				<Stack direction="row" alignItems="flextStart" spacing={2} data-sort-by={column.key} data-sort-dir="asc">
					{currSorting?.sortBy === column.key && currSorting?.sortDir === 'asc' ? (
						<AscActiveIcon />
					) : (
						<AscIcon />
					)}
					<Typography variant="bodyText">Sort A to Z</Typography>
				</Stack>
			</MenuItem>
			<MenuItem className="filter-row" onClick={handleFilterAction} data-sort-by={column.key} data-sort-dir="desc">
				<Stack direction="row" alignItems="flextStart" spacing={2} data-sort-by={column.key} data-sort-dir="desc">
					{currSorting?.sortBy === column.key && currSorting?.sortDir === 'desc' ? (
						<DescActiveIcon />
					) : (
						<DescIcon />
					)}
					<Typography variant="bodyText">Sort Z to A</Typography>
				</Stack>
			</MenuItem>
		</>
	);

	const renderSortMenuItems = () => {
		if (tableType === 'add-schedules') return null;
		return (
			<>
				{isPipelineTableType ? renderPipelineSortMenuItems() : renderDefaultSortMenuItems()}
			</>
		);
	};

	const renderClearColumnFilter = () => {
		const hasFilterData = filterSearchData?.[column.key] || dateTimeFields.has(column.key);
		if (!hasFilterData) return null;
		return (
			<div className="clear-filter-sec">
				<MenuItem
					className="filter-row"
					onClick={handleClearColunmAction}
					disabled={!currFilter?.[column.key]?.length}
				>
					<Stack direction="row" alignItems="flextStart" spacing={2}>
						<ClearColumnFilterIcon color={`${colors.dropdown.menuItemText} !important`} />
						<Typography variant="bodyText">Clear Column Filter</Typography>
					</Stack>
				</MenuItem>
			</div>
		);
	};

	const renderViewCountFilter = () => {
		if (column.key !== 'viewCount') return null;
		return (
			<div className="viewCount-filter-div">
				<FormControl className="viewCount-filter-formControl" sx={{ m: 1 }}>
					<Box
						sx={{
							backgroundColor: `${colors.dropdown.background}`,
							borderRadius: "4px",
							padding: "4px 8px",
							display: "inline-block",
						}}
					>
						<TextField
							label="Min View Count"
							type="number"
							value={minViewCount}
							onChange={(e) => handleViewCountChange('min', e.target.value)}
							inputProps={{ min: 0 }}
							variant="outlined"
							size="small"
							sx={{
								"& .MuiInputBase-root": {
									backgroundColor: `${colors.dropdown.background} !important`,
									color: `${colors.dropdown.menuItemHoverText} !important`,
								},
								"& .MuiInputBase-input": {
									color: `${colors.dropdown.menuItemHoverText} !important`,
								},
							}}
						/>
					</Box>
				</FormControl>
				<FormControl className="viewCount-filter-formControl" sx={{ m: 1 }}>
					<Box
						sx={{
							backgroundColor: `${colors.dropdown.background}`,
							borderRadius: "4px",
							padding: "4px 8px",
							display: "inline-block",
						}}
					>
						<TextField
							label="Max View Count"
							type="number"
							value={maxViewCount}
							onChange={(e) => handleViewCountChange('max', e.target.value)}
							inputProps={{ min: minViewCount === '' ? 0 : minViewCount }}
							variant="outlined"
							size="small"
							sx={{
								"& .MuiInputBase-root": {
									backgroundColor: `${colors.dropdown.background} !important`,
									color: `${colors.dropdown.menuItemHoverText} !important`,
								},
								"& .MuiInputBase-input": {
									color: `${colors.dropdown.menuItemHoverText} !important`,
								},
							}}
						/>
					</Box>
				</FormControl>
			</div>
		);
	};

	const renderDateTimePicker = (
		label: string,
		fieldName: string,
		dateTimeValue: any,
		extraProps?: Record<string, any>
	) => (
		<FormControl className="dateFilter-formControl" style={fieldName === 'toDateTime' ? { marginBottom: '0px' } : undefined} sx={{ m: 1 }}>
			<DateTimePicker
				label={label}
				disableFuture={jobName === "dashboard"}
				showToolbar={true}
				components={{
					OpenPickerIcon: DatePickerIcon,
				}}
				PopperProps={{
					placement: fieldName === 'fromDateTime' && jobName === "run_history" ? "top" : "auto",
					sx: {
						"& .MuiPaper-root": {
							backgroundColor: `${colors.dropdown.background} !important`,
						},
						"& .MuiPickersDay-root": {
							color: `${colors.dropdown.menuItemHoverText} !important`,
							"&.Mui-selected": {
								backgroundColor: `${colors.dropdown.menuItemSelected} !important`,
							},
							"&:hover": {
								backgroundColor: `${colors.dropdown.menuItemSelected} !important`,
							},
						},
						"& .MuiTypography-root": {
							...theme.typography.filterText,
							color: `${colors.dropdown.menuItemHoverText} !important`,
						},
					},
				}}
				toolbarTitle={
					<>
						Select Date Time{' '}
						<div className="time-picker-zone">{`Timezone: ${moment().isDST() ? 'EDT(UTC-4)' : 'EST(UTC-5)'
							}`}</div>
					</>
				}
				className="dateFilter"
				ampm={false}
				value={moment(dateTimeValue).tz('America/New_York')}
				onChange={(dateTime) => {
					handleDateTimeChange(fieldName, dateTime);
				}}
				renderInput={(params: any) => <TextField
					name={fieldName === 'fromDateTime' ? 'startDateTime' : 'endDateTime'}
					{...params}
					sx={{
						"& .MuiInputBase-root": {
							backgroundColor: `${colors.dropdown.background} !important`,
							color: `${colors.dropdown.menuItemHoverText} !important`,
						},
						"& .MuiInputBase-input": {
							color: `${colors.dropdown.menuItemHoverText} !important`,
						}
					}}
				/>}
				{...extraProps}
			/>
		</FormControl>
	);

	const renderDateTimeFilter = () => {
		if (column.key === 'viewCount' || !dateTimeFields.has(column.key)) return null;
		return (
			<div className="dateFilter-div">
				<LocalizationProvider className="outerDateContainer" dateAdapter={AdapterMoment}>
					{renderDateTimePicker('From Date', 'fromDateTime', fromDateTime)}
					{renderDateTimePicker('To Date', 'toDateTime', toDateTime, {
						minDate: (jobName === "dashboard") ? "" : moment(fromDateTime).tz('America/New_York')
					})}
				</LocalizationProvider>
			</div>
		);
	};

	const renderTextFilter = () => {
		if (column.key === 'viewCount' || dateTimeFields.has(column.key) || !filterSearchData?.[column.key]) return null;
		return (
			<div className="text-filter-sec">
				<div className="text-filter-row">
					<TextField
						id="searchField"
						autoComplete="off"
						variant="standard"
						placeholder={`Search ${typeof column.name === 'string' ? column.name : nodeToText(column.name)}`}
						className={classes.textfield1}
						sx={{
							backgroundColor: '#003771 !important',
							borderRadius: '4px',
							'& .MuiInputBase-input': {
								...theme.typography.filterText,
								'::placeholder': {
									...theme.typography.filterText,
								},
							}
						}}
						InputProps={{
							startAdornment: (
								<InputAdornment position="start">
									<img className={classes.searchBar} src={searchIcon} alt="search" />
								</InputAdornment>
							)
						}}
						inputProps={{
							style: {
								height: '25px',
								color: '#F5F5F5'
							}
						}}
						onChange={(e: any) => {
							debouncedResults(e);
						}}
					/>
				</div>
				<div className="text-filter-row">
					<div className="selectAll-sec">
						<MenuItem className="selectAll">
							<FormControlLabel
								className={classes.checkbox}
								label={<Typography variant="filterText">{`All (${filterSearchData[column.key]?.length || 0})`}</Typography>}
								control={
									<Checkbox
										color="success"
										value="all"
										checked={itemsChecked}
										onChange={(e) => {
											selectAllCheck(e);
										}}
									/>
								}
							/>
						</MenuItem>
						<div className="checkbox-sec">
							{filterSearchData[column.key]?.sort((a: any, b: any) => {
								// Check if 'a' or 'b' is checked (checked items should come first)
								const isCheckedA = checkedList.includes(a);
								const isCheckedB = checkedList.includes(b);
								if (isCheckedA && !isCheckedB) return -1; // 'a' should come before 'b'
								if (!isCheckedA && isCheckedB) return 1;  // 'b' should come before 'a'
								return 0; // If both are checked or both unchecked, no change in order
							}).map((filter: any) => {
								let filterLabel: any = filter;
								if (filter === true) {
									filterLabel = 'Yes';
								} else if (filter === false) {
									filterLabel = 'No';
								}
								return (
									<MenuItem key={filter}>
										<FormControlLabel
											className={classes.checkbox}
											label={<Typography variant="filterText">{filterLabel}</Typography>}
											control={
												<Checkbox
													color="success"
													value={filter}
													checked={
														(filter === true && checkedList.includes('true')) ||
														(filter === false && checkedList.includes('false')) ||
														checkedList.includes(filter) || checkedList.includes(filter.toString())
													}
													onChange={(e) => {
														handleChange(e);
													}}
												/>
											}
										/>
									</MenuItem>
								);
							})}
						</div>
					</div>
				</div>
			</div>
		);
	};

	const renderFilterButtons = () => {
		const showButtons = filterSearchData?.[column.key] || dateTimeFields.has(column.key) || column.key === 'viewCount';
		if (!showButtons) return null;
		return (
			<div className="buttonSec">
				<Button variant="contained" className={classes.cancelBtn} onClick={handleClose}>
					cancel
				</Button>
				<Button variant="contained" className={classes.okBtn} onClick={handleOkClick}>
					ok
				</Button>
			</div>
		);
	};

	const renderFilterIconSpan = () => {
		if (filterDisabledColumns.includes(column.key)) {
			return <span>&nbsp;</span>;
		}
		if (!columnFilterController) {
			const legacyIconSource = getSortIconSource(currSorting, column.key, isSortIcon, idleFilterIcon);
			return (
				<span
					role="button"
					data-header-key={column.key}
					onClick={filterPopup}
					tabIndex={0}
					onKeyDown={(e) => {
						if (e.key === 'Enter' || e.key === ' ') filterPopup(e);
					}}
				>
					{currFilter?.[column.key]?.length ? (
						<img src={FilterGreenDot} className="filter-green-dot" alt="filter active" />
					) : null}
					{legacyIconSource ? <img src={legacyIconSource} alt="sort icon" /> : null}
				</span>
			);
		}
		const filterState = columnFilterController?.getIconState(column.key);
		const headerSortDirection = currSorting?.sortBy === column.key ? currSorting.sortDir : undefined;
		const sortDirection = filterState?.sortDirection ?? headerSortDirection;
		const filterCount = (filterState?.filterCount ?? 0) +
			(filterState?.sortDirection || !sortDirection ? 0 : 1);
		const hasSort = Boolean(sortDirection);
		const filteredCount = Math.max(0, filterCount - Number(hasSort));
		const sortingLabel = sortDirection === 'asc' ? 'Sorting Up' : 'Sorting Down';
		const indicatorTooltip = filteredCount > 0
			? `${filteredCount} Filtered${hasSort ? ` & ${sortingLabel}` : ''}`
			: sortingLabel;
		return (
			<span // NOSONAR
				style={{ position: 'relative', float: 'right', marginTop: 8, display: 'inline-flex', alignItems: 'center', gap: 2 }}
				role="button"
				data-header-key={column.key}
				onClick={filterPopup}
				tabIndex={0}
				onKeyDown={(e) => {
					if (e.key === 'Enter' || e.key === ' ') {
						filterPopup(e);
					}
				}}
			>
	
				{hasSort ? (
					<Tooltip title={indicatorTooltip} arrow>
						<span style={{ display: 'inline-flex' }}>
							<FilterMenuIcon color="#FFA200" />
						</span>
					</Tooltip>
				) : (
					<FilterMenuIcon color="#39ACE7" />
				)}
				{filteredCount > 0 && (
					<Tooltip title={indicatorTooltip} arrow>
						<span
							aria-label={`${filteredCount} active filters`}
							style={{
								position: 'absolute',
								top: -9,
								right: -5,
								minWidth: 15,
								height: 15,
								padding: '0 4px',
								borderRadius: 8,
								background: '#FF9D00',
								color: '#fff',
								fontSize: 10,
								fontWeight: 700,
								lineHeight: '15px',
								textAlign: 'center',
							}}
						>
							{filteredCount}
						</span>
					</Tooltip>
				)}
			</span>
		);
	};

	return (
		<>
			<div // NOSONAR
				ref={(ref) => {
					drag(ref);
					drop(ref);
				}}
				style={{
					opacity: isDragging ? 0.5 : 1,
					backgroundColor: isOver ? '#0c0f25' : undefined,
					cursor: 'move'
				}}
				tabIndex={0} // NOSONAR
				onClick={(e) => e.stopPropagation()}
				onKeyDown={(e) => {
					if (e.key === 'Enter' || e.key === ' ') {
						e.stopPropagation();
					}
				}}
				onContextMenu={(e) => {
					e.preventDefault();
					e.stopPropagation();
					contextClick(e);
				}}
			>
				<div className={`${isSortIcon ? 'sort-icon' : 'filter-icon'}${tableType?.toLowerCase().startsWith('data-reconciliation-') ? ' reconciliation-header-renderer' : ''}`}>
					{renderHeaderContent()}
					{/* Header Dropdown - Show only for specific columns */}
					{renderHeaderDropdown()}

					{renderFilterIconSpan()}
				</div>
			</div>
			{/* <ClickAwayListener onClickAway={handleClose}> */}
			{/* <Dialog
					open={contextMenu !== null}
					onClose={handleClose}
					aria-labelledby="dialog-title"
					aria-describedby="dialog-description"
					onClick={(e) => e.stopPropagation()}
					onMouseDown={(e) => e.stopPropagation()}
					onContextMenu={(e) => e.stopPropagation()}
					sx={{
						zIndex: 9999,
					}}
					>
				<DialogTitle onClick={(e) => e.stopPropagation()}
			onContextMenu={(e) => e.stopPropagation()} id="dialog-title" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
					<CloseIcon fontSize="small" style={{ cursor: 'pointer' }} onClick={handleCloseIconClick} />
				</DialogTitle> */}

			{/* Dialog Content */}
			{/* <DialogContent onClick={(e) => e.stopPropagation()}
				onContextMenu={(e) => e.stopPropagation()} > */}
			{/* Select All Checkbox */}
			{/* <FormControlLabel
					control={<Checkbox checked={selectAll} onChange={(e) => handleSelectAllChange(e)} color="success" />}
					label="Select All"
					/> */}

			{/* Column Checkboxes */}
			{/* {columnNames.map((col: string, idx: number) => (
					<FormControlLabel
						key={col}
						control={
						<Checkbox
							checked={tempVisibleColumns.includes(col)}
							onChange={(e) => {
							e.stopPropagation();
							handleColumnChange(col, e);
							}}
							disabled={idx === 0}
							name={col}
							sx={{
							color: '#00D08D',
							'&.Mui-checked': {
								color: '#00D08D !important',
							},
							'&.Mui-disabled': {
								color: 'rgba(0, 0, 0, 0.80) !important',
							},
							}}
						/>
						}
						label={col}
					/>
					))}
				</DialogContent> */}

			{/* Dialog Actions (Buttons for Cancel/OK) */}
			{/* <DialogActions onClick={(e) => e.stopPropagation()}
				onContextMenu={(e) => e.stopPropagation()} >
					<Button onClick={handleCancelClick} color="primary">
					Cancel
					</Button>
					<Button onClick={handleSaveClick} color="primary">
					OK
					</Button>
				</DialogActions>
				</Dialog> */}
			{/* </ClickAwayListener> */}
			{/* </div> */}

			<Popover
				open={filterMenu !== null}
				onClose={handleClose}
				disableAutoFocus
				slotProps={{
					paper: {
						onMouseDown: (event: ReactMouseEvent<HTMLDivElement>) => event.stopPropagation(),
						onClick: (event: ReactMouseEvent<HTMLDivElement>) => event.stopPropagation(),
					},
				}}
				onMouseLeave={handleClose}
				className="filterContainer"
				anchorReference="anchorPosition"
				anchorPosition={filterMenu === null ? undefined : { top: filterMenu.mouseY, left: filterMenu.mouseX }}
				sx={{
					'& .MuiPaper-root': {
						overflow: 'visible',
						backgroundColor: `${colors.dropdown.menuItemBackground} !important`,
						color: `${colors.dropdown.menuItemText} !important`,
						...(!columnFilterController && {
							'& svg': {
								fill: `${colors.dropdown.menuItemText} !important`,
							},
						}),
						'& .MuiTypography-root': {
							color: columnFilterController ? `${colors.inputNew.inputText} !important` : `${colors.dropdown.menuItemText} !important`,
						},
						'& .MuiMenuItem-root': {
							color: `${colors.dropdown.menuItemText} !important`,
						},
						'& .MuiCheckbox-root svg': {
							color: `${colors.dropdown.menuItemText} !important`,
						},
					},
				}}
			>
				{enableColumnFilter && columnFilterController && columnFilterColumns ? (
					<ColumnFilterControl
						column={column as FilterableColumn}
						columns={columnFilterColumns}
						rows={columnFilterRows}
						controller={columnFilterController}
						isOpen={filterMenu !== null}
						getColumnValues={getColumnFilterValues}
						activeSortDirection={currSorting?.sortBy === column.key ? currSorting.sortDir : undefined}
						onClose={() => {
							setFilterMenu(null);
							setFilterPopupOpen('true');
						}}
					/>
				) : (
					<>
						{renderSortMenuItems()}
						{renderClearColumnFilter()}
						{renderViewCountFilter()}
						{renderDateTimeFilter()}
						{renderTextFilter()}
						{renderFilterButtons()}
					</>
				)}
			</Popover>
		</>
	);
}
